/**
 * The queue for a full tournament.
 *
 * THE TIER IS DERIVED HERE, not returned by the API. resolveOfferWindow picks
 * it server-side at the moment an offer goes out, and the waitlist rows carry
 * only the result (offered_at, expires_at). So the thresholds are mirrored
 * below — if WAITLIST_WINDOWS in functions/lib/tournaments.js changes, this
 * has to change with it.
 */

import { useEffect, useState } from 'react';
import { apiPost } from '../../lib/api.js';

/** Mirrors WAITLIST_WINDOWS in functions/lib/tournaments.js. */
const FAR_HOURS = 12;
const NEAR_HOURS = 2;
const ADMIN_ONLY_THRESHOLD_HOURS = 2;

const STATUS_VARIANTS = {
  waiting:   'neutral',
  offered:   'info',
  confirmed: 'success',
  expired:   'neutral',
};

/**
 * The tournament's start as a Date, or null.
 *
 * date is 'YYYY-MM-DD' and start_time is 'HH:MM' UTC per schema.sql, so they
 * are assembled as UTC rather than parsed as local — the same reason
 * lib/format.js refuses `new Date(string)` on a bare date.
 */
function startsAt(tournament) {
  if (!tournament.date) return null;
  const [y, m, d] = String(tournament.date).split('-').map(Number);
  if (!y || !m || !d) return null;

  const [hh, mm] = String(tournament.start_time ?? '19:00').split(':').map(Number);
  return new Date(Date.UTC(y, m - 1, d, hh || 0, mm || 0));
}

/** { adminRequired } or { hours } — the same shape resolveOfferWindow returns. */
function offerWindow(tournament, now) {
  const start = startsAt(tournament);
  if (start === null) return { hours: FAR_HOURS, hoursAway: null };

  const hoursAway = (start.getTime() - now.getTime()) / 3_600_000;

  if (hoursAway < ADMIN_ONLY_THRESHOLD_HOURS) return { adminRequired: true, hoursAway };
  return { hours: hoursAway > 24 ? FAR_HOURS : NEAR_HOURS, hoursAway };
}

/** '1h 04m', or null once it has run out. */
function countdown(expiresAt, now) {
  if (!expiresAt) return null;

  // SQLite datetimes have no zone marker; they are UTC per schema.sql.
  const expiry = new Date(`${String(expiresAt).replace(' ', 'T')}Z`);
  const ms = expiry.getTime() - now.getTime();
  if (Number.isNaN(ms) || ms <= 0) return null;

  const minutes = Math.floor(ms / 60_000);
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

export default function WaitlistTab({ venueId, tournamentId, tournament, waitlist, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError]   = useState(null);
  const [now, setNow]       = useState(() => new Date());

  // A countdown that does not count is just a number. One tick a minute is
  // enough for windows measured in hours.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const window = offerWindow(tournament, now);

  const confirm = async (row) => {
    setBusyId(row.id);
    setError(null);

    const { error: err } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/waitlist/confirm`,
      { waitlistId: row.id }
    );

    setBusyId(null);
    if (err) { setError(err); return; }
    await onChanged();
  };

  if (waitlist.length === 0) {
    return <div className="hd-empty">Nobody is waiting.</div>;
  }

  return (
    <div className="hd-stack">
      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <p className="hd-hint">
        {window.adminRequired
          ? 'Inside the two-hour cutoff — no automatic offers go out. Spots are filled from here.'
          : `Offers currently get ${window.hours} hours to be accepted.`}
      </p>

      <div className="hd-table-wrap">
        <table className="hd-table hd-admin-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Player</th>
              <th>Status</th>
              <th>Offer window</th>
              <th className="hd-table-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {waitlist.map((row) => {
              const remaining = countdown(row.expiresAt, now);

              return (
                <tr key={row.id}>
                  <td className="hd-muted">{row.position}</td>
                  <td>
                    {row.displayName}
                    {row.isGuest && <span className="hd-guest-tag">guest</span>}
                  </td>
                  <td>
                    <span className={`hd-badge hd-badge-${STATUS_VARIANTS[row.status] ?? 'neutral'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>
                    {window.adminRequired
                      ? <span className="hd-muted">Admin only</span>
                      : `${window.hours}h`}
                  </td>
                  <td className="hd-table-right">
                    {/* Under the cutoff there is no offer to wait on, so the
                        spot is handed over directly instead of counting down
                        to an expiry that was never set. */}
                    {row.status === 'confirmed' ? (
                      <span className="hd-muted">In</span>
                    ) : window.adminRequired ? (
                      <button
                        type="button"
                        className="hd-btn hd-btn-secondary hd-btn-sm"
                        disabled={busyId === row.id}
                        onClick={() => confirm(row)}
                      >
                        {busyId === row.id ? 'Confirming…' : 'Confirm spot'}
                      </button>
                    ) : row.status === 'offered' ? (
                      <span className={remaining ? '' : 'hd-muted'}>
                        {remaining ? `${remaining} left` : 'Expired'}
                      </span>
                    ) : (
                      <span className="hd-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
